import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { Back, BottomPlace, Spinner, Top } from './components/parts';
import { CardPage } from './pages/card';
import { ListPage } from './pages/list';
import { ScanPage } from './pages/scan';
import { ShowPage } from './pages/show';
import { ShowsPage } from './pages/shows';
import { answerOffer, loadMoving, offeredCount, onListChange, takeMoved } from './phone-list';
import { route, useLocation, type Route } from './router';
import { cards } from './words';

// Sign-in brings Firebase, so its page is a file of its own that loads only when the buyer opens it: the other pages
// stay small for a phone with little signal at the show.
const AccountPage = lazy(() => import('./pages/account').then((m) => ({ default: m.AccountPage })));

/** The website: the page for the address, drawn again when the address changes. */
export function App() {
  const r = route(useLocation());

  // The old pages' list, on its way here after #from= (phone-list.ts): the address drops the ids, they wait for the
  // buyer's Add (OldListOffer), and the cards the buyer added come in now, or when the phone has signal again.
  useEffect(() => {
    if (takeMoved(window.location.hash)) window.history.replaceState(null, '', window.location.pathname + window.location.search);
    void loadMoving();
    const again = () => void loadMoving();
    window.addEventListener('online', again);
    return () => window.removeEventListener('online', again);
  }, []);

  // The bars at the bottom (redteam #261 N2): a page's bars (Undo, the card page's Add bar) on top, the old list's
  // question under them, never one over the other. A bar that comes later goes above, so the question's buttons stay
  // where they are: the question shows at once, a card page's Add bar when the card has loaded.
  const [place, setPlace] = useState<HTMLDivElement | null>(null);

  // The page keeps room at its end for the bars' height (redteam #268 L1): with the question and Undo showing, the
  // last card's Remove still scrolls up above them. --bars is 0px while the bars are empty (hidden).
  const bars = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bars.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const seen = new ResizeObserver(() => root.style.setProperty('--bars', `${el.offsetHeight}px`));
    seen.observe(el);
    return () => {
      seen.disconnect();
      root.style.removeProperty('--bars');
    };
  }, []);

  return (
    <BottomPlace.Provider value={place}>
      <Page r={r} />
      <div className="bars" ref={bars}>
        <div className="page-bars" ref={setPlace} />
        <OldListOffer />
      </div>
    </BottomPlace.Provider>
  );
}

/**
 * The cards an address brought from the old pages, asked once on any page before they go on the list: any link can
 * carry #from= (redteam #260 L1). No drops them.
 */
function OldListOffer() {
  const count = useSyncExternalStore(onListChange, offeredCount, () => 0);
  if (!count) return null;
  return (
    <div className="offer" role="region" aria-label="Cards from your old list">
      <p>Add {cards(count)} from your old GrailFind list?</p>
      <div>
        <button type="button" className="no" onClick={() => answerOffer(false)}>
          No
        </button>
        <button
          type="button"
          onClick={() => {
            answerOffer(true);
            void loadMoving();
          }}>
          Add
        </button>
      </div>
    </div>
  );
}

/** The page for the address. */
function Page({ r }: { r: Route }) {
  switch (r.page) {
    case 'shows':
      return <ShowsPage query={r.query} />;
    case 'show':
      // A new show is a new page: nothing of the last show's filters or pages stays.
      return <ShowPage key={r.slug} slug={r.slug} query={r.query} />;
    case 'card':
      return <CardPage key={r.id} id={r.id} />;
    case 'list':
      return <ListPage />;
    case 'account':
      return (
        <Suspense
          fallback={
            <>
              <Top />
              <main>
                <Spinner />
              </main>
            </>
          }>
          <AccountPage query={r.query} />
        </Suspense>
      );
    case 'scan':
      return <ScanPage />;
    case 'missing':
      return <Missing />;
  }
}

/** An address the site has no page for: an old link, or a typo. */
function Missing() {
  return (
    <>
      <Top />
      <main>
        <Back href="/shows" label="All shows" />
        <h1>Page not found</h1>
        <p className="lede">This address has no page on GrailFind. The shows and their cards start from All shows.</p>
      </main>
    </>
  );
}
